// src/pages/Volunteers.jsx
import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, TextField,
  Grid, Card, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, MenuItem,
  TablePagination, Avatar, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, Snackbar, Alert, CircularProgress
} from '@mui/material';
import {
  Download as DownloadIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import apiEndpoints from '../../apiconfig';
import Tooltip from '@mui/material/Tooltip';
import Modal from '@mui/material/Modal';

const volunteerColumns = [
  { id: 'id', label: 'ID', minWidth: 70 },
  { id: 'name', label: 'Name', minWidth: 180 },
  { id: 'email', label: 'Email', minWidth: 200 },
  { id: 'phone_number', label: 'Phone Number', minWidth: 150 },
  { id: 'date_of_birth', label: 'Date of Birth', minWidth: 120 },
  { id: 'message', label: 'Message', minWidth: 250 },
  { id: 'actions', label: 'Actions', minWidth: 150, align: 'center' },
];

const Volunteers = () => {
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    email: '',
    phone_number: '',
  });
  const [selectedVolunteer, setSelectedVolunteer] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone_number: '',
    date_of_birth: '',
    message: ''
  });

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewError, setPreviewError] = useState(false);

  // Fetch volunteers from API
  useEffect(() => {
    const fetchVolunteers = async () => {
      try {
        const response = await fetch(apiEndpoints.volunteer);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();

        // Ensure data is always an array
        setVolunteers(Array.isArray(data) ? data : []);

      } catch (error) {
        console.error('Error fetching volunteers:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load volunteer data',
          severity: 'error'
        });
        // Set volunteers to empty array on error
        setVolunteers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVolunteers();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handlePreviewClick = (imagePath) => {
    setPreviewError(false);
    if (imagePath) {
      const fullImageUrl = `${apiEndpoints.idproof_path}${imagePath}`;
      setPreviewImage(fullImageUrl);
      setPreviewOpen(true);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSearch = (e) => setSearchText(e.target.value);

  const handleResetFilters = () => {
    setFilters({
      email: '',
      phone_number: '',
    });
    setSearchText('');
  };

  const handleViewProfile = (volunteer) => {
    setSelectedVolunteer(volunteer);
    setActiveTab(0);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedVolunteer(null);
    setEditMode(false);
    setFormData({
      name: '',
      email: '',
      phone_number: '',
      date_of_birth: '',
      message: ''
    });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleExport = () => {
    try {
      const headers = ['ID', 'Name', 'Email', 'Phone Number', 'Date of Birth', 'Message'];
      const csvContent = [
        headers.join(','),
        ...filteredVolunteers.map(volunteer =>
          [
            volunteer.id,
            `"${volunteer.name}"`,
            volunteer.email,
            volunteer.phone_number,
            formatDate(volunteer.date_of_birth),
            `"${volunteer.message}"`,
          ].join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `volunteers_export_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbar({
        open: true,
        message: 'Volunteer data exported successfully!',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to export volunteer data',
        severity: 'error'
      });
    }
  };

  const handleAddNew = () => {
    setEditMode(false);
    setFormData({
      name: '',
      email: '',
      phone_number: '',
      date_of_birth: '',
      message: ''
    });
    setOpenDialog(true);
  };

  const handleEdit = (volunteer) => {
    setEditMode(true);
    setFormData({
      name: volunteer.name,
      email: volunteer.email,
      phone_number: volunteer.phone_number,
      date_of_birth: volunteer.date_of_birth,
      message: volunteer.message
    });
    setSelectedVolunteer(volunteer);
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${apiEndpoints.volunteer}?id=${id}`, {
        method: 'DELETE'
      });
      const result = await response.json();

      if (result.message) {
        setVolunteers(volunteers.filter(v => v.id !== id));
        setSnackbar({
          open: true,
          message: 'Volunteer deleted successfully',
          severity: 'success'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to delete volunteer',
        severity: 'error'
      });
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let url = apiEndpoints.volunteer;
      let method = 'POST';

      if (editMode && selectedVolunteer) {
        url += `?id=${selectedVolunteer.id}`;
        method = 'PUT';
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      const result = await response.json();

      if (result.message) {
        // Refresh the list
        const updatedResponse = await fetch(apiEndpoints.volunteer);
        const updatedData = await updatedResponse.json();
        setVolunteers(updatedData);

        setSnackbar({
          open: true,
          message: editMode ? 'Volunteer updated successfully' : 'Volunteer added successfully',
          severity: 'success'
        });
        handleCloseDialog();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to ${editMode ? 'update' : 'add'} volunteer`,
        severity: 'error'
      });
    }
  };

  const filteredVolunteers = Array.isArray(volunteers)
    ? volunteers.filter(volunteer => {
      return (
        (filters.email ? volunteer.email.toLowerCase().includes(filters.email.toLowerCase()) : true) &&
        (filters.phone_number ? volunteer.phone_number.includes(filters.phone_number) : true) &&
        (searchText ?
          volunteer.name.toLowerCase().includes(searchText.toLowerCase()) ||
          volunteer.email.toLowerCase().includes(searchText.toLowerCase()) ||
          volunteer.phone_number.includes(searchText) ||
          volunteer.message.toLowerCase().includes(searchText.toLowerCase())
          : true)
      );
    })
    : [];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>Volunteers</Typography>

      <Card sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search volunteers..."
              value={searchText}
              onChange={handleSearch}
              InputProps={{
                startAdornment: <SearchIcon color="action" />,
              }}
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Email"
              name="email"
              value={filters.email}
              onChange={handleFilterChange}
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Phone Number"
              name="phone_number"
              value={filters.phone_number}
              onChange={handleFilterChange}
            />
          </Grid>

          <Grid item xs={12} md={2} display="flex" gap={1}>
            <Button
              variant="outlined"
              color="success"
              startIcon={<FilterListIcon />}
              onClick={handleResetFilters}
              sx={{ flex: 1 }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              sx={{ flex: 1 }}
            >
              Export
            </Button>
          </Grid>
        </Grid>
      </Card>

      <Box sx={{ mb: 2 }}>
        {/* <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
        >
          Add New Volunteer
        </Button> */}
      </Box>

      <Card>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {volunteerColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    style={{ minWidth: column.minWidth, fontWeight: 600 }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredVolunteers.length > 0 ? (
                filteredVolunteers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={row.id}
                    >
                      {volunteerColumns.map((column) => {
                        const value = row[column.id];

                        if (column.id === 'id') {
                          return (
                            <TableCell
                              key={column.id}
                              align={column.align}
                              sx={{ color: 'success.main', fontWeight: 500 }}
                            >
                              {value}
                            </TableCell>
                          );
                        }

                        if (column.id === 'name') {
                          return (
                            <TableCell key={column.id} align={column.align}>
                              <Box display="flex" alignItems="center">
                                <Avatar sx={{ width: 36, height: 36, mr: 2, bgcolor: 'primary.main' }}>
                                  {row.name.charAt(0)}
                                </Avatar>
                                {value}
                              </Box>
                            </TableCell>
                          );
                        }

                        if (column.id === 'email') {
                          return (
                            <TableCell key={column.id} align={column.align}>
                              <Typography variant="body2">{value}</Typography>
                            </TableCell>
                          );
                        }

                        if (column.id === 'phone_number') {
                          return (
                            <TableCell key={column.id} align={column.align}>
                              <Typography variant="body2">{value || 'N/A'}</Typography>
                            </TableCell>
                          );
                        }

                        if (column.id === 'date_of_birth') {
                          return (
                            <TableCell key={column.id} align={column.align}>
                              <Typography variant="body2">{formatDate(value)}</Typography>
                            </TableCell>
                          );
                        }

                        if (column.id === 'message') {
                          return (
                            <TableCell key={column.id} align={column.align}>
                              <Typography variant="body2" sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {value}
                              </Typography>
                            </TableCell>
                          );
                        }

                        if (column.id === 'actions') {
                          return (
                            <TableCell key={column.id} align="center">
                              {row.id_proof_path && (
                                <Tooltip title="Preview ID Proof" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => handlePreviewClick(row.id_proof_path)}
                                    color="primary"
                                    sx={{ mr: 1 }}
                                  >
                                    <VisibilityIcon fontSize="small" color="info" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(row.id)}
                                color="primary"
                              >
                                <DeleteIcon fontSize="small" color="error" />
                              </IconButton>
                            </TableCell>
                          );
                        }

                        return (
                          <TableCell key={column.id} align={column.align}>
                            {value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={volunteerColumns.length} align="center">
                    <Typography variant="body1" color="textSecondary">
                      No volunteers found matching your criteria
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredVolunteers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      {/* Volunteer Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {editMode ? `Edit Volunteer: #${selectedVolunteer?.id}` : 'Add New Volunteer'}
            </Typography>
            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  name="date_of_birth"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.date_of_birth}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Message"
                  name="message"
                  multiline
                  rows={4}
                  value={formData.message}
                  onChange={handleFormChange}
                />
              </Grid>
            </Grid>
            <DialogActions sx={{ mt: 2 }}>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" variant="contained" color="primary">
                {editMode ? 'Update' : 'Save'}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
      <Modal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        aria-labelledby="image-preview-modal"
        aria-describedby="image-preview-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 2,
            outline: 'none',
            maxWidth: '90vw',
            maxHeight: '90vh'
          }}
        >
          {previewError ? (
            <Typography variant="h6" color="error" sx={{ p: 4 }}>
              Failed to load image preview
            </Typography>
          ) : (
            <img
              src={previewImage}
              alt="ID Proof"
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(80vh - 64px)',
                display: 'block',
                margin: '0 auto'
              }}
              onError={() => setPreviewError(true)}
            />
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              onClick={() => setPreviewOpen(false)}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Volunteers;